import { useEffect, useRef } from 'react';

import { getAppAccessCodeRequest } from 'store/auth/actions';
import { GET_APP_ACCESS_CODE } from 'store/auth/actionTypes';
import { appAccessCodeSelector } from 'store/auth/selectors';
import { createProcessingSelector } from 'store/loading/selectors';

import { sendAnalyticAccessCodeLoaded } from 'services/Analytics/trackers/accessCode';

import { useDispatch, useSelector } from 'hooks/store';

import CopyCodeDesktop from 'components/InstallAppSteps/InstallAppStepsA/components/CopyCodeStep/components/CopyCodeDesktop';
import CopyCodeMobile from 'components/InstallAppSteps/InstallAppStepsA/components/CopyCodeStep/components/CopyCodeMobile';

const CopyCodeStep = ({ isMobileDevice }: { isMobileDevice: boolean }) => {
    const dispatch = useDispatch();

    const isLoading = useSelector(createProcessingSelector([GET_APP_ACCESS_CODE]));
    const appAccessCodeData = useSelector(appAccessCodeSelector);
    const { access_code, expires_at } = appAccessCodeData ?? {};
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (access_code) {
            sendAnalyticAccessCodeLoaded(isMobileDevice);
        } else {
            dispatch(getAppAccessCodeRequest());
        }
    }, [dispatch, access_code, isMobileDevice]);

    useEffect(() => {
        if (expires_at && access_code) {
            const expiresAtTimestamp = new Date(expires_at).getTime();
            const currentTimestamp = Date.now();

            const timeout = expiresAtTimestamp - currentTimestamp - 60000;

            if (timeout > 0) {
                timeoutRef.current = setTimeout(() => {
                    dispatch(getAppAccessCodeRequest());
                }, timeout);
            } else {
                dispatch(getAppAccessCodeRequest());
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [expires_at, access_code, dispatch]);

    if (!access_code) return null;

    if (isMobileDevice) return <CopyCodeMobile accessCode={access_code} isLoading={isLoading} />;

    return <CopyCodeDesktop accessCode={access_code} isLoading={isLoading} />;
};

export default CopyCodeStep;
